import {
  ArrowDown01Icon,
  Clock01Icon,
  Factory01Icon,
  Refresh01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAssetStatusSegments } from '@/components/features/analytics/hooks/use-asset-status-segments';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { assetStatusesApi } from '@/lib/asset-status-client';

export const Route = createFileRoute('/_authenticated/analytics/asset-status-segments')({
  component: AssetStatusSegmentsPage,
});

function AssetStatusSegmentsPage() {
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 30);
    return yesterday.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState<string>('00:00');
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [endTime, setEndTime] = useState<string>('23:59');
  const [isAssetDropdownOpen, setIsAssetDropdownOpen] = useState(false);
  const assetDropdownRef = useRef<HTMLDivElement>(null);
  const [assetsData, setAssetsData] = useState<Array<{ id: string; name: string; code: string }>>(
    [],
  );

  // Fetch assets for the dropdown
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const data = await assetStatusesApi.getAssetStatuses({ limit: 1000 });
        // Extract asset info from segments data
        const uniqueAssets = new Map<string, { id: string; name: string; code: string }>();
        data.data.forEach((segment: any) => {
          segment.segments?.forEach((seg: any) => {
            if (seg.assetId) {
              uniqueAssets.set(seg.assetId, {
                id: seg.assetId,
                name: seg.assetName || 'Unknown',
                code: seg.assetCode || '',
              });
            }
          });
        });
        setAssetsData(Array.from(uniqueAssets.values()));
      } catch (error) {
        console.error('Error fetching assets:', error);
      }
    };
    fetchAssets();
  }, []);

  // Combine date and time for API calls
  const startDateTime = startDate && startTime ? `${startDate}T${startTime}:00` : undefined;
  const endDateTime = endDate && endTime ? `${endDate}T${endTime}:59` : undefined;

  // Fetch all segments (API will filter by date range)
  const { data, isLoading, error, refetch } = useAssetStatusSegments(
    undefined,
    startDateTime,
    endDateTime,
  );

  // Handle clicking outside asset dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assetDropdownRef.current && !assetDropdownRef.current.contains(event.target as Node)) {
        setIsAssetDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter segments based on selected assets
  const filteredData = useMemo(() => {
    if (!data?.data) return null;

    // If no assets selected, show all
    if (selectedAssets.length === 0) {
      return data;
    }

    // Filter to only show selected assets
    return {
      ...data,
      data: data.data.filter((asset) => selectedAssets.includes(asset.assetId)),
    };
  }, [data, selectedAssets]);

  const handleRefresh = () => {
    refetch();
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = (seconds % 60).toFixed(2);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-destructive">Error loading asset status segments</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Asset Status Segments</h1>
          <p className="text-muted-foreground">
            Timeline view of status changes per asset with duration-based segments
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div>
            <div className="flex flex-wrap items-end gap-4">
              {/* Asset Selector */}
              <div className="space-y-2 min-w-[200px]">
                <Label htmlFor="asset-select">Select Assets</Label>
                <div className="relative" ref={assetDropdownRef}>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => setIsAssetDropdownOpen(!isAssetDropdownOpen)}
                  >
                    <span>
                      {selectedAssets.length === 0
                        ? 'All Assets'
                        : selectedAssets.length === 1
                          ? assetsData.find((m) => m.id === selectedAssets[0])?.name ||
                            'Selected Asset'
                          : `${selectedAssets.length} assets selected`}
                    </span>
                    <HugeiconsIcon icon={ArrowDown01Icon} className="size-4" />
                  </Button>

                  {isAssetDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                      <div className="p-2">
                        <div className="space-y-1">
                          <label className="flex items-center space-x-2 p-2 hover:bg-secondary/20 rounded cursor-pointer">
                            <Checkbox
                              checked={selectedAssets.length === 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedAssets([]);
                                }
                              }}
                            />
                            <span className="text-sm">All Assets</span>
                          </label>

                          {assetsData.map((asset) => (
                            <label
                              key={asset.id}
                              className="flex items-center space-x-2 p-2 hover:bg-secondary/20 rounded cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedAssets.includes(asset.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedAssets([...selectedAssets, asset.id]);
                                  } else {
                                    setSelectedAssets(
                                      selectedAssets.filter((id) => id !== asset.id),
                                    );
                                  }
                                }}
                              />
                              <span className="text-sm">
                                {asset.name} ({asset.code})
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Date Range Selection */}
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-time">Start Time</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-32"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time">End Time</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-32"
                />
              </div>

              {/* Refresh Button */}
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="h-8"
                >
                  <HugeiconsIcon
                    icon={Refresh01Icon}
                    className={`size-4 ${isLoading ? 'animate-spin' : ''}`}
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Segments Timeline */}
      {filteredData?.data && filteredData.data.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Status Segments Timeline</CardTitle>
            <CardDescription>
              Timeline view of status changes per asset with duration-based segments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {filteredData.data.map((asset, index) => (
                <AssetTimelineCard
                  key={asset.assetId}
                  asset={asset}
                  startDateTime={startDateTime}
                  endDateTime={endDateTime}
                  formatTime={formatTime}
                  formatDuration={formatDuration}
                  isLast={index === filteredData.data.length - 1}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : !isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <HugeiconsIcon icon={Clock01Icon} className="size-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Segments Found</h3>
            <p className="text-muted-foreground text-center">
              No status segments found for the selected assets and time range. Try adjusting your
              selection or time period.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span>Loading data...</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Component for segments with cursor-following tooltips
function SegmentTimeline({
  segments,
  clampSegmentToRange,
  timelineStart,
  timelineEnd,
  totalTimelineMs,
  formatTime,
  formatDuration,
}: {
  segments: Array<{
    id: string;
    statusId: string;
    statusName: string;
    statusColor: string;
    statusIsFailure: boolean;
    statusIsRunning: boolean;
    startTime: string;
    endTime: string | null;
    durationSeconds: number | null;
    notes: string | null;
  }>;
  clampSegmentToRange: (
    segment: { startTime: string; endTime: string | null },
    rangeStart: Date,
    rangeEnd: Date,
  ) => {
    clampedStart: Date;
    clampedEnd: Date;
    durationMs: number;
  };
  timelineStart: Date;
  timelineEnd: Date;
  totalTimelineMs: number;
  formatTime: (date: Date) => string;
  formatDuration: (seconds: number) => string;
}) {
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);

  const segmentsWithPosition = useMemo(() => {
    return segments
      .map((segment) => {
        const clamped = clampSegmentToRange(segment, timelineStart, timelineEnd);

        // Skip if no overlap
        if (clamped.durationMs <= 0) return null;

        // Calculate position and width based on clamped duration
        const leftPosition =
          ((clamped.clampedStart.getTime() - timelineStart.getTime()) / totalTimelineMs) * 100;
        const width = (clamped.durationMs / totalTimelineMs) * 100;

        return {
          segment,
          clamped,
          leftPosition: Math.max(0, Math.min(100, leftPosition)),
          width: Math.max(0.1, Math.min(100, width)), // Minimum 0.1% width for visibility
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [segments, clampSegmentToRange, timelineStart, timelineEnd, totalTimelineMs]);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseLeave = () => {
    setHoveredSegment(null);
    setMousePosition(null);
  };

  return (
    <div
      ref={timelineContainerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="absolute inset-0"
    >
      {segmentsWithPosition.map((item) => (
        <div
          key={item.segment.id}
          className="absolute top-0 h-full transition-all duration-200 hover:scale-y-110 cursor-pointer z-10"
          style={{
            left: `${item.leftPosition}%`,
            width: `${item.width}%`,
            backgroundColor: item.segment.statusColor || '#000000',
            opacity: 0.8,
          }}
          onMouseEnter={() => setHoveredSegment(item.segment.id)}
        />
      ))}
      {hoveredSegment && mousePosition && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            left: `${mousePosition.x + 10}px`,
            top: `${mousePosition.y - 10}px`,
            transform: 'translateY(-100%)',
          }}
        >
          {(() => {
            const segment = segmentsWithPosition.find((s) => s.segment.id === hoveredSegment);
            if (!segment) return null;
            return (
              <div className="rounded-md px-3 py-1.5 text-xs bg-foreground text-background shadow-lg border">
                <div className="space-y-1">
                  <div className="font-medium">{segment.segment.statusName || 'Unknown'}</div>
                  <div className="text-sm">
                    Duration: {formatDuration(Math.floor(segment.clamped.durationMs / 1000))}
                  </div>
                  <div className="text-sm">Start: {formatTime(segment.clamped.clampedStart)}</div>
                  <div className="text-sm">End: {formatTime(segment.clamped.clampedEnd)}</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function AssetTimelineCard({
  asset,
  startDateTime,
  endDateTime,
  formatTime,
  formatDuration,
  isLast = false,
}: {
  asset: {
    assetId: string;
    assetName: string;
    assetCode: string;
    assetType: string;
    segments: Array<{
      id: string;
      statusId: string;
      statusName: string;
      statusColor: string;
      statusIsFailure: boolean;
      statusIsRunning: boolean;
      startTime: string;
      endTime: string | null;
      durationSeconds: number | null;
      notes: string | null;
    }>;
    statusSummary: Array<{
      statusId: string;
      statusName: string;
      statusColor: string;
      statusIsFailure: boolean;
      statusIsRunning: boolean;
      totalDurationSeconds: number;
      totalDurationFormatted: string;
      segmentCount: number;
      percentage: number;
    }>;
    totalSegments: number;
    totalDurationSeconds: number;
  };
  startDateTime?: string;
  endDateTime?: string;
  formatTime: (date: Date) => string;
  formatDuration: (seconds: number) => string;
  isLast?: boolean;
}) {
  // Use provided date/time or fallback to segment times
  const timelineStart = startDateTime
    ? new Date(startDateTime)
    : asset.segments[0]?.startTime
      ? new Date(asset.segments[0].startTime)
      : new Date();

  const timelineEnd = endDateTime ? new Date(endDateTime) : new Date();

  const totalTimelineMs = timelineEnd.getTime() - timelineStart.getTime();
  const now = new Date();

  // Clamp segment to time range and calculate accurate duration
  const clampSegmentToRange = (
    segment: {
      startTime: string;
      endTime: string | null;
    },
    rangeStart: Date,
    rangeEnd: Date,
  ) => {
    const segStart = new Date(segment.startTime);
    const rawEnd = segment.endTime ? new Date(segment.endTime) : now;
    const segEnd = rawEnd > rangeEnd ? rangeEnd : rawEnd;

    // Check if segment overlaps with range at all
    if (segEnd < rangeStart || segStart > rangeEnd) {
      return { clampedStart: rangeStart, clampedEnd: rangeStart, durationMs: 0 };
    }

    const clampedStart = segStart < rangeStart ? rangeStart : segStart;
    const clampedEnd = segEnd > rangeEnd ? rangeEnd : segEnd;

    const durationMs = Math.max(0, clampedEnd.getTime() - clampedStart.getTime());
    return { clampedStart, clampedEnd, durationMs };
  };

  // Sort segments by start time
  const sortedSegments = [...asset.segments].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );

  // Calculate status breakdown with clamped durations
  const statusBreakdown = useMemo(() => {
    const breakdown: Record<
      string,
      { count: number; totalDuration: number; color: string; statusName: string }
    > = {};

    sortedSegments.forEach((segment) => {
      const { durationMs } = clampSegmentToRange(segment, timelineStart, timelineEnd);
      if (durationMs <= 0) return;

      const statusName = segment.statusName || 'Unknown';
      if (!breakdown[statusName]) {
        breakdown[statusName] = {
          count: 0,
          totalDuration: 0,
          color: segment.statusColor || '#000000',
          statusName,
        };
      }

      breakdown[statusName].count++;
      breakdown[statusName].totalDuration += Math.floor(durationMs / 1000);
    });

    return breakdown;
  }, [sortedSegments, timelineStart, timelineEnd, clampSegmentToRange]);

  const totalAssetDuration = Object.values(statusBreakdown).reduce(
    (sum, status) => sum + status.totalDuration,
    0,
  );

  return (
    <div className="space-y-4">
      {/* Asset Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <HugeiconsIcon icon={Factory01Icon} className="size-5 text-blue-500" />
          <div>
            <h3 className="text-lg font-semibold">{asset.assetName}</h3>
            <p className="text-sm text-muted-foreground">
              {asset.assetCode} • {asset.assetType}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">Total Segments</div>
          <div className="text-lg font-semibold">{asset.totalSegments}</div>
        </div>
      </div>

      {/* Timeline Container */}
      <div className="relative">
        {/* Timeline Scale */}
        <div className="flex justify-between text-xs text-muted-foreground mb-2">
          <span>{formatTime(timelineStart)}</span>
          <span>{formatTime(new Date(timelineStart.getTime() + totalTimelineMs / 2))}</span>
          <span>{formatTime(timelineEnd)}</span>
        </div>

        {/* Timeline Bar */}
        <div className="relative h-12 bg-secondary/20 rounded-lg overflow-hidden border">
          {/* Time Grid Lines */}
          <div className="absolute inset-0 flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex-1 border-r border-border last:border-r-0" />
            ))}
          </div>

          {/* Segments */}
          <SegmentTimeline
            segments={sortedSegments}
            clampSegmentToRange={clampSegmentToRange}
            timelineStart={timelineStart}
            timelineEnd={timelineEnd}
            totalTimelineMs={totalTimelineMs}
            formatTime={formatTime}
            formatDuration={formatDuration}
          />
        </div>

        {/* Now Indicator */}
        {(() => {
          const nowMs = now.getTime();
          const timelineStartMs = timelineStart.getTime();
          const timelineEndMs = timelineEnd.getTime();

          // Only show if current time is within the timeline range
          if (nowMs >= timelineStartMs && nowMs <= timelineEndMs) {
            const nowPosition =
              ((nowMs - timelineStartMs) / (timelineEndMs - timelineStartMs)) * 100;

            return (
              <div
                className="absolute top-0 h-12 w-0.5 bg-black z-20"
                style={{ left: `${nowPosition}%` }}
              >
                {/* Now Label */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap z-30">
                  Now
                </div>
                {/* Arrow pointing down */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-black"></div>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Segment Summary */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-secondary/20 px-4 py-2 border-b">
          <h4 className="font-medium text-sm text-foreground">Status Summary</h4>
        </div>
        <div className="flex items-stretch flex-nowrap overflow-x-auto">
          {Object.entries(statusBreakdown)
            .sort((a, b) => b[1].totalDuration - a[1].totalDuration)
            .map(([statusName, summary], idx, entries) => {
              const percentage =
                totalAssetDuration > 0 ? (summary.totalDuration / totalAssetDuration) * 100 : 0;

              return (
                <div key={statusName} className="flex items-stretch">
                  <div className="px-4 py-3 hover:bg-secondary/20 flex items-center gap-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="size-4 rounded-full"
                        style={{ backgroundColor: summary.color }}
                      />
                      <div>
                        <div className="font-medium text-sm">{summary.statusName}</div>
                        <div className="text-xs text-muted-foreground">
                          {summary.count} segment{summary.count !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-sm">
                        {formatDuration(summary.totalDuration)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {percentage.toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                  {idx < entries.length - 1 && (
                    <Separator orientation="vertical" className="h-auto mx-2" />
                  )}
                </div>
              );
            })}
        </div>
      </div>
      {!isLast && <Separator className="w-full bg-primary" />}
    </div>
  );
}
